"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { MOT_COOKIE_PREFERENCES_CHANGED } from "@/components/CookieConsent";
import { isAnalyticsAllowed } from "@/lib/analytics-consent";
import { reportGa4SpaPageview } from "@/lib/ga4-pageviews";
import { isProdPublicTrackingEnabled } from "@/lib/public-deploy-env";

export function Ga4SpaPageviews() {
  const prod = isProdPublicTrackingEnabled();
  const pathname = usePathname();
  const prevPathRef = useRef<string | null>(null);
  const [allowed, setAllowed] = useState(false);

  const syncAllowed = useCallback(() => {
    setAllowed(isAnalyticsAllowed());
  }, []);

  useEffect(() => {
    if (!prod) {
      return;
    }
    syncAllowed();
    function onPrefs() {
      syncAllowed();
    }
    window.addEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPrefs);
    return () => window.removeEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPrefs);
  }, [prod, syncAllowed]);

  useEffect(() => {
    if (!prod) {
      return;
    }
    if (!allowed) {
      prevPathRef.current = null;
      return;
    }

    const prev = prevPathRef.current;
    if (prev === null) {
      prevPathRef.current = pathname;
      return;
    }
    if (prev === pathname) {
      return;
    }

    const reported = reportGa4SpaPageview(pathname, prev);
    if (reported !== null) {
      prevPathRef.current = pathname;
      return;
    }

    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      if (cancelled) return;
      const retryReported = reportGa4SpaPageview(pathname, prev);
      if (retryReported !== null) {
        prevPathRef.current = pathname;
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [prod, allowed, pathname]);

  return null;
}
